import * as Sentry from '@sentry/react-native';
import type { ApiResponse } from 'apisauce';

/**
 * ── The one error surface for every API call ────────────────────────────
 *
 * apisauce never throws — it hands back an `ApiResponse` with `ok: false` and a
 * `problem`. This module turns that into a single thrown `ApiError`, so every
 * caller writes one `catch` instead of re-deriving "what kind of failure was
 * this" from `problem`/`status`/`data` at each call site.
 *
 * The backend has ONE error envelope, always:
 *   {"error": {"code": "conflict", "message": "…", "field": "email", "details": {}}}
 * `code` is stable and machine-readable; `message` is written to be shown to the
 * user verbatim; `field` appears only when the failure belongs to one form field.
 *
 * ── The four kinds, and why the message differs per kind ────────────────
 * The rule we settled on: a 4xx means the CLIENT sent something wrong, so the
 * server's own `message` is the best possible text and we show it as-is. Nothing
 * else is:
 *
 *   'api'     4xx with a parseable envelope → `message` is the SERVER's, shown verbatim.
 *   'server'  5xx → the server broke. Its message (a stack trace, a proxy's HTML)
 *             is an implementation detail, never user-facing → generic text.
 *   'network' the request never got a response at all → tell them it's their
 *             connection, which is actionable, rather than blaming the app.
 *   'unknown' a response we can't read (HTML error page, empty body, cancel).
 *
 * `kind` exists so a caller can treat these differently WITHOUT re-sniffing
 * status codes — e.g. only an 'api' error is worth pinning to a form field,
 * because only that kind carries `field`.
 *
 * Callers branch on `status` / `code` for the handful of non-2xx responses that
 * are a normal part of a flow (see authApi's login 404). Everything else just
 * shows `message`.
 * ───────────────────────────────────────────────────────────────────────
 */

export type ApiErrorKind = 'api' | 'server' | 'network' | 'unknown';

// Deliberately blames the connection, not the app: a request that never reached
// a server is the one failure the user can actually do something about.
export const NETWORK_MESSAGE =
  "Your connection doesn't seem to be working. Check it and try again.";
export const SERVER_MESSAGE =
  'Something went wrong on our end. Please try again.';
export const UNKNOWN_MESSAGE = 'Something went wrong. Please try again.';

/**
 * The inside of the backend's `{"error": …}` envelope, typed as `unknown` per
 * field. It arrives from the network, so nothing about it is guaranteed — the
 * readers below check each field rather than trusting the shape.
 */
type ErrorEnvelope = {
  code?: unknown;
  message?: unknown;
  field?: unknown;
  details?: unknown;
};

function readEnvelope(data: unknown): ErrorEnvelope | null {
  if (typeof data !== 'object' || data === null) return null;
  const { error } = data as { error?: unknown };
  if (typeof error !== 'object' || error === null) return null;
  return error as ErrorEnvelope;
}

// An empty string is as useless as a missing one (it would render a blank
// toast), so both collapse to null and fall through to the generic message.
const readString = (value: unknown): string | null =>
  typeof value === 'string' && value.length > 0 ? value : null;

export class PSErrorClass extends Error {
  readonly kind: ApiErrorKind;
  /** The envelope's stable `code`. Null unless `kind` is 'api'. */
  readonly code: string | null;
  /** The form field this failure belongs to, when the server named one. */
  readonly field: string | null;
  /** The HTTP status, or undefined when no response arrived (network errors). */
  readonly status: number | undefined;
  readonly details: unknown;

  constructor(init: {
    kind: ApiErrorKind;
    message: string;
    status?: number;
    code?: string | null;
    field?: string | null;
    details?: unknown;
  }) {
    super(init.message);
    this.name = 'ApiError';
    this.kind = init.kind;
    this.status = init.status;
    this.code = init.code ?? null;
    this.field = init.field ?? null;
    this.details = init.details;
  }
}

/** Classify a failed apisauce response. See the module docstring for the kinds. */
export function extractErrorFromResponse<T>(
  response: ApiResponse<T>
): PSErrorClass {
  const { problem, status, data } = response;

  // No HTTP response at all — there's no status and no body to read, so this
  // has to be decided on `problem` before anything else.
  if (
    problem === 'NETWORK_ERROR' ||
    problem === 'CONNECTION_ERROR' ||
    problem === 'TIMEOUT_ERROR'
  ) {
    return new PSErrorClass({
      kind: 'network',
      message: NETWORK_MESSAGE,
      status,
    });
  }

  // Checked BEFORE parsing the envelope: a 5xx may well carry one, but showing
  // a server-side failure's message to the user is exactly what we don't want.
  if (status !== undefined && status >= 500) {
    return new PSErrorClass({
      kind: 'server',
      message: SERVER_MESSAGE,
      status,
    });
  }

  const envelope = readEnvelope(data);
  if (envelope) {
    const message = readString(envelope.message);
    // No message means we can't honour "show the server's text", so this isn't
    // an 'api' error we can present — fall through to the generic one.
    if (message) {
      return new PSErrorClass({
        kind: 'api',
        message,
        status,
        code: readString(envelope.code),
        field: readString(envelope.field),
        details: envelope.details,
      });
    }
  }

  return new PSErrorClass({
    kind: 'unknown',
    message: UNKNOWN_MESSAGE,
    status,
  });
}

/**
 * Coerce anything caught into an `ApiError`.
 *
 * `handleApiError` covers the failures the SERVER reports, but a `.catch()` in a
 * hook catches more than that: an endpoint function that reads a 200 body which
 * doesn't match its declared type throws a plain `TypeError` (the types here are
 * a claim, not a check — see api.ts), and so does a bug in the mapping around
 * it. Those reach the same `catch`.
 *
 * So this is what lets a hook state `error: ApiError | null` and MEAN it — a UI
 * branching on `error.kind` can't be handed something with no `kind`. An
 * unrecognised throw is exactly the 'unknown' case the kinds already describe:
 * generic copy, because we have nothing user-facing to say about it.
 *
 * The original is kept on `details` (and logged) rather than used as `message` —
 * "Cannot read properties of undefined" is a developer's sentence, not a user's.
 */
export function asApiError(error: unknown): PSErrorClass {
  if (error instanceof PSErrorClass) return error;
  // Reaching here means the throw was NOT one of ours, which narrows it to a
  // client bug — a 200 body that didn't match its declared type, or the mapping
  // around it. Unfiltered on purpose: there's no benign case, and the original's
  // own stack is the whole value. Razorpay can't reach this either; it RETURNS
  // cancellations and throws PSErrorClass for the rest.
  Sentry.captureException(error, { tags: { api_kind: 'client-throw' } });
  return new PSErrorClass({
    kind: 'unknown',
    message: UNKNOWN_MESSAGE,
    details: error,
  });
}

// An id in the path would mint a NEW Sentry issue per membership, booking and
// event, so every one collapses before it reaches a fingerprint. Matches whole
// segments only — a uuid or a digit run — so `/me/memberships` survives intact.
const ID_SEGMENT =
  /\/(?:[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}|\d+)(?=\/|$)/gi;

const routeOf = <T>(response: ApiResponse<T>): string => {
  const method = response.config?.method?.toUpperCase() ?? 'UNKNOWN';
  const path = (response.config?.url ?? 'unknown').replace(ID_SEGMENT, '/:id');
  return `${method} ${path}`;
};

/**
 * Throw the classified error for a failed response. Returns `never`, which is
 * what lets a caller write `if (!res.ok || !res.data) handleApiError(res);` and
 * have TypeScript narrow `res.data` to defined on the lines below.
 */
export function handleApiError<T>(response: ApiResponse<T>): never {
  const error = extractErrorFromResponse(response);
  console.debug(
    `API Error [${error.kind}]`,
    response.status,
    error.code,
    error.message
  );
  // 'server' ONLY. A 4xx is the server saying we asked wrong, and several are a
  // normal part of a flow (login's 404, a wrong OTP, a precondition error); a
  // 'network' error is the user's connection, which we can't act on and which
  // would be the highest-volume event in the project by far. Reporting either
  // buries the 5xx that this exists to surface.
  if (error.kind === 'server') {
    const route = routeOf(response);
    Sentry.captureException(error, {
      // Load-bearing. `message` is the constant SERVER_MESSAGE and the stack is
      // always this function, so the default grouping files EVERY 5xx in the app
      // as one issue. Group by the endpoint that actually broke instead.
      fingerprint: ['api-server-error', route, String(error.status)],
      tags: { api_route: route, api_status: String(error.status) },
    });
  }
  throw error;
}
